/**
 * TableBrowser — Collapsible table/column tree for the code editor.
 *
 * Shows all workspace tables. Expand a table to see its columns + types.
 * Click a table name to insert it. Click a column to insert "table"."column".
 */
import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
  alpha,
  Collapse,
  useTheme,
} from '@mui/material';
import {
  TableChart as TableIcon,
  ViewColumn as ColumnIcon,
  ExpandMore as ExpandIcon,
  ChevronRight as CollapseIcon,
  Search as SearchIcon,
  ContentCopy as InsertIcon,
} from '@mui/icons-material';
import type { WorkspaceTable } from '../../services/workspaceApi';

// ── Types ────────────────────────────────────────────────────────────

interface TableBrowserProps {
  /** Available workspace tables with column metadata. */
  tables: WorkspaceTable[];
  /** Called when user clicks a table or column to insert into editor. */
  onInsert: (text: string) => void;
  /** Table names currently referenced in the SQL (for highlighting). */
  referencedTables?: Set<string>;
  /** Currently selected input table names (for checkbox selection). */
  selectedInputTables?: string[];
  /** Called when input table selection changes. */
  onInputTablesChange?: (tables: string[]) => void;
}

// ── Component ────────────────────────────────────────────────────────

const TableBrowser: React.FC<TableBrowserProps> = ({ tables, onInsert, referencedTables, selectedInputTables, onInputTablesChange }) => {
  const hasInputSelection = !!onInputTablesChange;
  const selectedSet = useMemo(() => new Set(selectedInputTables || []), [selectedInputTables]);
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedTables, setExpandedTables] = useState<Set<string>>(new Set());

  const toggleTable = useCallback((name: string) => {
    setExpandedTables((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  const filteredTables = useMemo(() => {
    if (!searchQuery.trim()) return tables;
    const q = searchQuery.toLowerCase();
    return tables.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        t.columns?.some((c: { name: string }) => c.name.toLowerCase().includes(q)),
    );
  }, [tables, searchQuery]);

  return (
    <Box
      sx={{
        width: 220,
        minWidth: 220,
        borderRight: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box sx={{ px: 1.5, py: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Tables
        </Typography>
      </Box>

      {/* Search */}
      <Box sx={{ px: 1, py: 0.5 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Filter..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          autoComplete="off"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
              </InputAdornment>
            ),
            sx: { fontSize: 12, py: 0 },
          }}
          sx={{ '& .MuiOutlinedInput-root': { height: 28 } }}
        />
      </Box>

      {/* Table list */}
      <List dense sx={{ overflowY: 'auto', flex: 1, py: 0 }}>
        {filteredTables.length === 0 && (
          <Box sx={{ px: 2, py: 2, textAlign: 'center' }}>
            <Typography variant="caption" color="text.disabled">
              No tables
            </Typography>
          </Box>
        )}
        {filteredTables.map((table) => {
          const isExpanded = expandedTables.has(table.name);
          const isReferenced = referencedTables?.has(table.name);
          const isSelected = selectedSet.has(table.name);
          const columns = table.columns || [];

          return (
            <React.Fragment key={table.name}>
              <ListItemButton
                onClick={() => toggleTable(table.name)}
                sx={{
                  py: 0.25,
                  px: hasInputSelection ? 0.5 : 1,
                  bgcolor: isSelected
                    ? alpha(theme.palette.primary.main, 0.1)
                    : isReferenced ? alpha(theme.palette.primary.main, 0.06) : undefined,
                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
                }}
              >
                {hasInputSelection && (
                  <Checkbox
                    size="small"
                    checked={isSelected}
                    onClick={(e) => {
                      e.stopPropagation();
                      const next = isSelected
                        ? (selectedInputTables || []).filter((t) => t !== table.name)
                        : [...(selectedInputTables || []), table.name];
                      onInputTablesChange!(next);
                    }}
                    sx={{ p: 0.25, mr: 0.25 }}
                  />
                )}
                <ListItemIcon sx={{ minWidth: 20 }}>
                  {isExpanded ? (
                    <ExpandIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  ) : (
                    <CollapseIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  )}
                </ListItemIcon>
                <ListItemIcon sx={{ minWidth: 20 }}>
                  <TableIcon sx={{ fontSize: 14, color: isReferenced ? 'primary.main' : 'text.secondary' }} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Tooltip title={table.name} placement="right" enterDelay={500}>
                      <Typography
                        variant="caption"
                        noWrap
                        fontWeight={isReferenced ? 600 : 400}
                        color={isReferenced ? 'primary.main' : 'text.primary'}
                        sx={{ fontSize: 11 }}
                      >
                        {table.name}
                      </Typography>
                    </Tooltip>
                  }
                />
                <Tooltip title="Insert table name">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onInsert(`"${table.name}"`);
                    }}
                    sx={{ p: 0.25, opacity: 0.5, '&:hover': { opacity: 1 } }}
                  >
                    <InsertIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                </Tooltip>
              </ListItemButton>

              <Collapse in={isExpanded}>
                {columns.map((col: { name: string; type?: string }) => (
                  <ListItemButton
                    key={col.name}
                    onClick={() => onInsert(`"${table.name}"."${col.name}"`)}
                    sx={{ py: 0.125, pl: 5, pr: 1 }}
                  >
                    <ListItemIcon sx={{ minWidth: 18 }}>
                      <ColumnIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" sx={{ fontSize: 11 }} noWrap>
                            {col.name}
                          </Typography>
                          {col.type && (
                            <Typography variant="caption" sx={{ fontSize: 9, color: 'text.disabled' }}>
                              {col.type}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItemButton>
                ))}
                {columns.length === 0 && (
                  <Typography variant="caption" color="text.disabled" sx={{ pl: 5, py: 0.5, display: 'block', fontSize: 10 }}>
                    No columns loaded
                  </Typography>
                )}
              </Collapse>
            </React.Fragment>
          );
        })}
      </List>
    </Box>
  );
};

export default TableBrowser;
